import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  CalendarMonth as CalendarIcon,
  Chat as ChatIcon,
  Star as StarIcon,
  BarChart as BarChartIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
  Logout,
  Delete as DeleteIcon,
  Lock as LockIcon,
  EmailOutlined as EmailOutlinedIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  AssignmentInd as AssignmentIndIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChaaviChangePasswordModal from './ChaaviChangePasswordModal';
import { APP_DISPLAY_NAME } from '../config/brand';
import AuthService from '../services/authService';
import VendorService from '../services/vendorService';
import BusinessService from '../services/businessService';

/** Design tokens from chaavi_vendor_dashboard.html */
export const CHA = {
  cream: '#f5f2ec',
  sidebar: '#3a2e1e',
  sidebarText: 'rgba(232,213,176,0.65)',
  sidebarTextHi: '#e8d5b0',
  sidebarActiveBg: 'rgba(192,135,58,0.18)',
  sidebarActiveText: '#e8c87a',
  accent: '#c0873a',
  accentHover: '#a87030',
  ink: '#3a2e1e',
  muted: '#a89880',
  border: '#e8e0d4',
  topbarMuted: '#d6c9b0',
};

/** `#a87030` fill, white label — Add / select image buttons in vendor forms. */
export const chaaviGoldAddImageButtonSx = {
  bgcolor: CHA.accentHover,
  color: '#fff',
  textTransform: 'none' as const,
  boxShadow: 'none',
  '&:hover': { bgcolor: CHA.accent, color: '#fff', boxShadow: 'none' },
};

export type ChaaviVendorNavKey = 'dashboard' | 'business' | 'availability' | 'messages' | 'reviews' | 'analytics';

interface ChaaviVendorDashboardShellProps {
  children: React.ReactNode;
  userFirstName?: string;
  footerLabel?: string;
  unreadChatCount: number;
  unreadNotificationCount: number;
  onOpenNotifications: () => void;
  onAddBusiness: () => void;
  /** Current nav highlight (sidebar). */
  activeNav: ChaaviVendorNavKey;
  onNav: (key: ChaaviVendorNavKey) => void;
  /** Override default dashboard headline; omit for “My business dashboard”. */
  pageTitle?: string;
  /** Override default subtitle (date line). */
  pageSubtitle?: string;
  /** Hide the “Add business” action (e.g. availability page). */
  showAddBusiness?: boolean;
}

const navButtonSx = (active: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  py: 1.25,
  px: 1.75,
  borderRadius: '8px',
  mx: 0.75,
  my: 0.125,
  width: 'calc(100% - 12px)',
  justifyContent: 'flex-start',
  textTransform: 'none' as const,
  fontSize: '13px',
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  color: active ? CHA.sidebarActiveText : CHA.sidebarText,
  bgcolor: active ? CHA.sidebarActiveBg : 'transparent',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.12s',
  '&:hover': {
    bgcolor: active ? CHA.sidebarActiveBg : 'rgba(255,255,255,0.07)',
    color: CHA.sidebarTextHi,
  },
});

const ChaaviVendorDashboardShell: React.FC<ChaaviVendorDashboardShellProps> = ({
  children,
  userFirstName = 'Vendor',
  footerLabel = 'Vendor',
  unreadChatCount,
  unreadNotificationCount,
  onOpenNotifications,
  onAddBusiness,
  activeNav,
  onNav,
  pageTitle,
  pageSubtitle,
  showAddBusiness = true,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenu, setMobileMenu] = useState<null | HTMLElement>(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [vendorCategories, setVendorCategories] = useState<string[]>([]);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  const displayName = user?.firstName || userFirstName || 'Vendor';
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || displayName;
  const initial = (fullName || 'V').charAt(0).toUpperCase();

  useEffect(() => {
    if (!user?.phoneNumber || user.userType !== 'VENDOR') {
      setVendorCategories([]);
      return;
    }
    (async () => {
      try {
        const businesses = await BusinessService.getBusinessesByVendorPhoneNumber(user.phoneNumber);
        const cats = businesses.map((b) => b.businessCategory).filter(Boolean) as string[];
        setVendorCategories(Array.from(new Set(cats)));
      } catch {
        setVendorCategories([]);
      }
    })();
  }, [user?.phoneNumber, user?.userType]);

  const handleProfileMenuClose = () => setProfileMenuAnchor(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
   
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setChangingPassword(true);
      setPasswordError('');
      await AuthService.changePassword(currentPassword, newPassword);
      setSnackbarMessage('Password changed successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setChangingPassword(false);
      setChangePasswordDialogOpen(false);
      handleProfileMenuClose();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to change password';
      setPasswordError(errorMessage);
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.phoneNumber) return;
    try {
      setDeletingAccount(true);
      await VendorService.deleteVendor(user.phoneNumber, user.phoneNumber);
      setSnackbarMessage('Your account and all related data have been deleted successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeletingAccount(false);
      setDeleteAccountDialogOpen(false);
      handleProfileMenuClose();
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 2000);
    } catch (err: any) {
      console.error('Error deleting account:', err);
      let errorMessage = 'Failed to delete account.';
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDeletingAccount(false);
    }
  };

  const sidebarItems = (
    <>
      <Typography
        sx={{
          px: 1.25,
          pt: 1.25,
          pb: 0.5,
          fontSize: '10px',
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: 'rgba(232,213,176,0.4)',
          fontFamily: 'sans-serif',
        }}
      >
        Main
      </Typography>
      <Button
        disableRipple
        sx={navButtonSx(activeNav === 'dashboard')}
        onClick={() => onNav('dashboard')}
        startIcon={<DashboardIcon sx={{ fontSize: 15, opacity: 0.9 }} />}
      >
        Dashboard
      </Button>
      <Button
        disableRipple
        sx={navButtonSx(activeNav === 'business')}
        onClick={() => onNav('business')}
        startIcon={<BusinessIcon sx={{ fontSize: 15, opacity: 0.9 }} />}
      >
        Business
      </Button>
      <Button
        disableRipple
        sx={navButtonSx(activeNav === 'availability')}
        onClick={() => onNav('availability')}
        startIcon={<CalendarIcon sx={{ fontSize: 15, opacity: 0.9 }} />}
      >
        Availability
      </Button>
      <Typography
        sx={{
          px: 1.25,
          pt: 1,
          pb: 0.5,
          fontSize: '10px',
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: 'rgba(232,213,176,0.4)',
          fontFamily: 'sans-serif',
        }}
      >
        Engage
      </Typography>
      <Button
        disableRipple
        sx={navButtonSx(activeNav === 'messages')}
        onClick={() => onNav('messages')}
        startIcon={<ChatIcon sx={{ fontSize: 15, opacity: 0.9 }} />}
      >
        Messages
        {unreadChatCount > 0 && (
          <Box
            component="span"
            sx={{
              ml: 'auto',
              minWidth: 17,
              height: 17,
              px: 0.25,
              borderRadius: '50%',
              bgcolor: CHA.accent,
              color: '#fff',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'sans-serif',
            }}
          >
            {unreadChatCount > 9 ? '9+' : unreadChatCount}
          </Box>
        )}
      </Button>
      <Button
        disableRipple
        sx={navButtonSx(activeNav === 'reviews')}
        onClick={() => onNav('reviews')}
        startIcon={<StarIcon sx={{ fontSize: 15, opacity: 0.9 }} />}
      >
        Reviews
      </Button>
      <Button
        disableRipple
        sx={navButtonSx(activeNav === 'analytics')}
        onClick={() => onNav('analytics')}
        startIcon={<BarChartIcon sx={{ fontSize: 15, opacity: 0.9 }} />}
      >
        Analytics
      </Button>
    </>
  );

  return (
    <>
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: { xs: '100dvh', md: '100vh' },
        bgcolor: CHA.cream,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        /* Full bleed inside main (no negative margins — global padding is already 0 on vendor routes). */
        boxSizing: 'border-box',
        borderRadius: 0,
      }}
    >
      {/* Desktop sidebar */}
      <Box
        sx={{
          width: 220,
          flexShrink: 0,
          bgcolor: CHA.sidebar,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          p: 0,
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 2.25,
            borderBottom: '0.5px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: CHA.sidebarTextHi,
            fontSize: 16,
            fontFamily: 'Georgia, serif',
          }}
        >
          <span style={{ fontSize: 20 }}>🧡</span>
          {APP_DISPLAY_NAME.toLowerCase()}
        </Box>
        <Box sx={{ py: 1 }}>{sidebarItems}</Box>
        <Box sx={{ mt: 'auto', py: 1.75, px: 1.25, borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
          <Button
            disableRipple
            sx={{
              ...navButtonSx(false),
              width: '100%',
              mx: 0,
            }}
            startIcon={<PersonIcon sx={{ fontSize: 15, opacity: 0.9 }} />}
            onClick={() => navigate('/vendor-dashboard')}
          >
            {footerLabel}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flex: '1 1 0%',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* Mobile nav trigger */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: CHA.sidebar,
            color: CHA.topbarMuted,
            px: 1.5,
            py: 1,
            borderBottom: '0.5px solid rgba(255,255,255,0.06)',
          }}
        >
          <IconButton sx={{ color: CHA.topbarMuted }} onClick={(e) => setMobileMenu(e.currentTarget)} size="small">
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontFamily: 'Georgia, serif', color: CHA.sidebarTextHi, fontSize: 15 }}>
            🧡 {APP_DISPLAY_NAME.toLowerCase()}
          </Typography>
          <Box width={40} />
        </Box>
        <Menu
          anchorEl={mobileMenu}
          open={Boolean(mobileMenu)}
          onClose={() => setMobileMenu(null)}
          PaperProps={{ sx: { bgcolor: CHA.sidebar, minWidth: 220 } }}
        >
          <Box sx={{ py: 0.5 }} onClick={() => setMobileMenu(null)}>
            {sidebarItems}
          </Box>
        </Menu>

        <Box
          sx={{
            height: 54,
            width: '100%',
            boxSizing: 'border-box',
            bgcolor: CHA.sidebar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 3,
            gap: 1.5,
            borderBottom: '0.5px solid rgba(255,255,255,0.06)',
          }}
        >
          <IconButton
            onClick={onOpenNotifications}
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.07)',
              color: CHA.topbarMuted,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.13)' },
            }}
          >
            <Badge
              color="warning"
              variant="dot"
              invisible={unreadNotificationCount === 0}
              sx={{ '& .MuiBadge-dot': { bgcolor: CHA.accent, border: `1.5px solid ${CHA.sidebar}` } }}
            >
              <NotificationsIcon sx={{ fontSize: 18, color: CHA.topbarMuted }} />
            </Badge>
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: CHA.topbarMuted, fontSize: 13 }}>
            <span>Welcome, {displayName}</span>
            <IconButton
              id="chaavi-vendor-profile-trigger"
              size="small"
              onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
              aria-label="Account menu"
              aria-controls={Boolean(profileMenuAnchor) ? 'chaavi-vendor-account-menu' : undefined}
              aria-haspopup="true"
              sx={{
                p: 0,
                borderRadius: '50%',
                '&:hover': { opacity: 0.92 },
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: CHA.accent,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                {initial}
              </Box>
            </IconButton>
            <Menu
              id="chaavi-vendor-account-menu"
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{
                'aria-labelledby': 'chaavi-vendor-profile-trigger',
                sx: { py: 0 },
              }}
              PaperProps={{
                sx: {
                  minWidth: 288,
                  maxWidth: 340,
                  mt: 0.75,
                  borderRadius: '14px',
                  overflow: 'hidden',
                  border: `1px solid ${CHA.border}`,
                  boxShadow: '0 12px 40px rgba(58,46,30,0.15)',
                  bgcolor: '#fff',
                },
              }}
            >
              {/* Profile view — matches public/vendor_profile_dropdown.html */}
              <Box
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.preventDefault()}
                sx={{
                  px: 2.25,
                  pt: 2,
                  pb: 2,
                  bgcolor: CHA.cream,
                  borderBottom: `1px solid ${CHA.border}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: CHA.accent,
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontFamily: 'Georgia, serif',
                    }}
                  >
                    {initial}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Georgia, serif',
                        fontSize: 17,
                        fontWeight: 600,
                        color: CHA.ink,
                        lineHeight: 1.25,
                        mb: 0.5,
                      }}
                      noWrap
                      title={fullName}
                    >
                      {fullName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                      <EmailOutlinedIcon sx={{ fontSize: 14, color: CHA.topbarMuted, mt: '2px', flexShrink: 0 }} />
                      <Typography
                        sx={{ fontSize: 12, color: CHA.topbarMuted, fontFamily: 'sans-serif', lineHeight: 1.35 }}
                        noWrap
                        title={user?.email ?? undefined}
                      >
                        {user?.email ?? '—'}
                      </Typography>
                    </Box>
                    {user?.phoneNumber ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, mt: 0.5 }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 14, color: CHA.muted, mt: '2px', flexShrink: 0 }} />
                        <Typography
                          sx={{ fontSize: 12, color: CHA.muted, fontFamily: 'sans-serif', lineHeight: 1.35 }}
                          noWrap
                        >
                          {user.phoneNumber}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ px: 2.25, py: 1.5, bgcolor: '#fff' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: vendorCategories.length > 0 ? 1.25 : 0,
                  }}
                >
                  <AssignmentIndIcon sx={{ fontSize: 18, color: CHA.muted }} />
                  <Typography sx={{ fontSize: 12, color: CHA.muted, fontFamily: 'sans-serif' }}>Role</Typography>
                  <Typography
                    sx={{
                      ml: 'auto',
                      fontSize: 12,
                      fontWeight: 600,
                      color: CHA.ink,
                      fontFamily: 'sans-serif',
                    }}
                  >
                    {user?.userType ?? 'VENDOR'}
                  </Typography>
                </Box>
                {vendorCategories.length > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: CHA.muted,
                        fontFamily: 'sans-serif',
                        mb: 0.75,
                      }}
                    >
                      Business categories
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {vendorCategories.map((category, index) => (
                        <Chip
                          key={`${category}-${index}`}
                          label={category}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: 11,
                            fontFamily: 'sans-serif',
                            bgcolor: 'rgba(192,135,58,0.08)',
                            color: CHA.accentHover,
                            border: '1px solid rgba(192,135,58,0.35)',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Box>

              <Divider sx={{ borderColor: CHA.border }} />

              <MenuItem
                onClick={() => {
                  handleProfileMenuClose();
                  setChangePasswordDialogOpen(true);
                }}
                sx={{
                  py: 1.25,
                  px: 2,
                  fontSize: 13,
                  color: CHA.ink,
                  fontFamily: 'sans-serif',
                  '&:hover': { bgcolor: 'rgba(245,242,236,0.85)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LockIcon fontSize="small" sx={{ color: CHA.accentHover }} />
                </ListItemIcon>
                Change Password
              </MenuItem>
              <Divider sx={{ borderColor: CHA.border }} />
              <MenuItem
                onClick={() => {
                  handleProfileMenuClose();
                  setDeleteAccountDialogOpen(true);
                }}
                sx={{
                  py: 1.25,
                  px: 2,
                  fontSize: 13,
                  fontFamily: 'sans-serif',
                  color: 'error.main',
                  '&:hover': { bgcolor: 'rgba(198,40,40,0.06)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                Delete Account
              </MenuItem>
              <Divider sx={{ borderColor: CHA.border }} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.25,
                  px: 2,
                  fontSize: 13,
                  color: CHA.ink,
                  fontFamily: 'sans-serif',
                  '&:hover': { bgcolor: 'rgba(245,242,236,0.85)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Logout fontSize="small" sx={{ color: CHA.accentHover }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            pb: 5,
            overflow: 'auto',
            flex: 1,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 2.75,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 500,
                  color: CHA.ink,
                  fontFamily: 'Georgia, serif',
                }}
              >
                {pageTitle ?? 'My business dashboard'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: CHA.muted, mt: 0.5, fontFamily: 'sans-serif' }}>
                {pageSubtitle ??
                  `Here's your business overview for today, ${new Date().toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}.`}
              </Typography>
            </Box>
            {showAddBusiness && (
              <Button
                onClick={onAddBusiness}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.9,
                  py: 1.1,
                  px: 2,
                  borderRadius: '8px',
                  bgcolor: CHA.accent,
                  color: '#fff',
                  fontSize: 13,
                  textTransform: 'none',
                  fontFamily: 'sans-serif',
                  '&:hover': { bgcolor: CHA.accentHover },
                }}
                startIcon={<Typography sx={{ fontSize: 18, fontWeight: 300, lineHeight: 1 }}>+</Typography>}
              >
                Add business
              </Button>
            )}
          </Box>

          {children}
        </Box>
      </Box>
    </Box>

    <ChaaviChangePasswordModal
      open={changePasswordDialogOpen}
      onClose={() => {
        if (changingPassword) return;
        setChangePasswordDialogOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
      }}
      currentPassword={currentPassword}
      newPassword={newPassword}
      confirmPassword={confirmPassword}
      onCurrentPasswordChange={(v) => {
        setCurrentPassword(v);
        setPasswordError('');
      }}
      onNewPasswordChange={(v) => {
        setNewPassword(v);
        setPasswordError('');
      }}
      onConfirmPasswordChange={(v) => {
        setConfirmPassword(v);
        setPasswordError('');
      }}
      submitting={changingPassword}
      serverError={passwordError}
      onSubmit={() => void handleChangePassword()}
    />

    <Dialog
      open={deleteAccountDialogOpen}
      onClose={() => !deletingAccount && setDeleteAccountDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle color="error">Delete Vendor Account</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete your account?
          <br />
          <br />
          <strong>Note:</strong> You cannot delete your account if you have pending or incomplete orders. Please complete or
          cancel all orders before deleting.
          <br />
          <br />
          <strong>This action cannot be undone. You will be logged out immediately.</strong>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteAccountDialogOpen(false)} disabled={deletingAccount} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => void handleDeleteAccount()}
          color="error"
          variant="contained"
          disabled={deletingAccount}
          startIcon={deletingAccount ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {deletingAccount ? 'Deleting Account...' : 'Delete My Account'}
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ChaaviVendorDashboardShell;
